// Shared stacked cost-over-time chart. One consistent axis/tooltip/legend style
// for every FinOps cost chart. Takes the /finops/v2 cost/timeseries payload
// ({ timestamps, series:[{key,total,points}] }) and renders stacked areas by
// entity (stable entity-keyed colors), with:
//   * a Rate ↔ Cumulative toggle (cumulative = per-series running sum),
//   * an optional dashed forecast line (linear trend of the cumulative total),
//   * a tooltip showing each series value + its share of the bucket total.
// One y-axis only; ≥2 series always get a legend (identity never color-alone).

import React, { useMemo, useState } from 'react';
import {
  AreaChart, Area, Line, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { assignColors, formatMoney, formatTimeTick, formatDateTime } from './finopsFormat';
import { Segmented, ChartLegend } from './finopsUi';
import { useChartTheme } from './chartTheme';

function CustomTooltip({ active, payload, label, windowHours }) {
  if (!active || !payload || !payload.length) return null;
  // Sum only the stacked cost series (exclude the projection line and data
  // gaps — null means "no samples", not $0).
  const stacked = payload.filter((p) => p.dataKey !== '__projected__' && p.value != null);
  if (!stacked.length) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/96 px-3.5 py-3 shadow-xl backdrop-blur-md">
        <div className="text-[13px] font-semibold text-slate-100 mb-1">{formatDateTime(label)}</div>
        <div className="text-xs text-slate-400">No samples in this bucket</div>
      </div>
    );
  }
  const total = stacked.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/96 px-3.5 py-3 shadow-xl backdrop-blur-md min-w-[180px]">
      <div className="text-[13px] font-semibold text-slate-100 mb-2">{formatDateTime(label)}</div>
      <div className="space-y-1">
        {stacked.slice().reverse().map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-slate-200">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
              {p.dataKey}
            </span>
            <span className="tabular-nums text-slate-300">
              {formatMoney(p.value)}
              {total > 0 && <span className="text-slate-500 ml-1.5">{Math.round((p.value / total) * 100)}%</span>}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-4 text-xs mt-2 pt-2 border-t border-slate-700">
        <span className="text-slate-400">Total</span>
        <span className="tabular-nums font-semibold text-slate-100">{formatMoney(total)}</span>
      </div>
    </div>
  );
}

export default function CostTimeseriesChart({ data, windowHours, height = 300, showForecast = false }) {
  const [mode, setMode] = useState('rate'); // 'rate' | 'cumulative'
  const theme = useChartTheme();

  const { rows, keys, colors, projKeyPresent } = useMemo(() => {
    if (!data || !data.series || !data.timestamps) return { rows: [], keys: [], colors: {}, projKeyPresent: false };
    const keys = data.series.map((s) => s.key);
    const colors = assignColors(keys);
    // pivot timestamps × series → recharts rows
    const running = {};
    const rows = data.timestamps.map((ts, i) => {
      const row = { t: ts };
      let bucketTotal = 0;
      data.series.forEach((s) => {
        const raw = s.points[i]; // null = NO samples in this bucket (a data gap)
        if (mode === 'cumulative') {
          // Cumulative through a gap = flat line (no spend observed) — honest
          // and keeps the running total continuous.
          running[s.key] = (running[s.key] || 0) + (raw || 0);
          row[s.key] = running[s.key];
        } else {
          // Rate mode: a gap stays null so the area BREAKS instead of lying
          // down on the zero line ("the chart shows $0" for hours we simply
          // didn't observe — LIVE-CAUGHT 2026-07-09).
          row[s.key] = raw == null ? null : raw;
        }
        bucketTotal += (raw || 0);
      });
      row.__total__ = bucketTotal;
      return row;
    });

    // Forecast: linear trend of the cumulative grand total, extended ~15% of the
    // window as a dashed projection (cumulative mode only — a projected running
    // total is meaningful; a projected instantaneous rate is noise).
    let projKeyPresent = false;
    if (showForecast && mode === 'cumulative' && rows.length >= 4) {
      const cum = rows.map((r) => keys.reduce((a, k) => a + (r[k] || 0), 0));
      const n = cum.length;
      const xs = cum.map((_, i) => i);
      const meanX = xs.reduce((a, b) => a + b, 0) / n;
      const meanY = cum.reduce((a, b) => a + b, 0) / n;
      let num = 0, den = 0;
      xs.forEach((x, i) => { num += (x - meanX) * (cum[i] - meanY); den += (x - meanX) ** 2; });
      const slope = den ? num / den : 0;
      const last = cum[n - 1];
      const extra = Math.max(2, Math.round(n * 0.15));
      // anchor projection to the last actual point, then extend
      rows[n - 1].__projected__ = last;
      const stepMs = rows.length > 1 ? new Date(rows[1].t).getTime() - new Date(rows[0].t).getTime() : 3600000;
      for (let j = 1; j <= extra; j++) {
        rows.push({ t: new Date(new Date(rows[n - 1].t).getTime() + j * stepMs).toISOString(),
                    __projected__: last + slope * j });
      }
      projKeyPresent = true;
    }
    return { rows, keys, colors, projKeyPresent };
  }, [data, mode, showForecast]);

  if (!rows.length) return null;

  const Chart = projKeyPresent ? ComposedChart : AreaChart;

  const legendItems = [
    ...keys.map((k) => ({ label: k, color: colors[k] })),
    ...(projKeyPresent ? [{ label: 'Projected', color: theme.neutralLine, dash: true }] : []),
  ];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <ChartLegend items={legendItems} className="min-w-0" />
        <Segmented
          options={[{ value: 'rate', label: 'Rate' }, { value: 'cumulative', label: 'Cumulative' }]}
          value={mode} onChange={setMode}
        />
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <Chart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
          <CartesianGrid {...theme.grid} vertical={false} />
          <XAxis dataKey="t" {...theme.axis} tickFormatter={(v) => formatTimeTick(v, windowHours)} minTickGap={40} />
          <YAxis {...theme.axis} tickFormatter={(v) => formatMoney(v, { compact: true, cents: false })} width={54} />
          <Tooltip content={<CustomTooltip windowHours={windowHours} />} cursor={theme.cursor} />
          {keys.map((k) => (
            <Area key={k} type="monotone" dataKey={k} stackId="cost"
                  stroke={colors[k]} strokeWidth={1.5} fill={colors[k]} fillOpacity={0.8}
                  connectNulls={false} isAnimationActive={false} />
          ))}
          {projKeyPresent && (
            <Line type="monotone" dataKey="__projected__" name="Projected"
                  stroke={theme.neutralLine} strokeWidth={1.5} strokeDasharray="5 4"
                  dot={false} isAnimationActive={false} connectNulls />
          )}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}
